import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms'
import dayGridPlugin from '@fullcalendar/daygrid'
import frLocale from '@fullcalendar/core/locales/fr'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { Draggable } from '@fullcalendar/interaction'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { CalendarModule } from 'primeng/calendar'
import { CardModule } from 'primeng/card'
import { ChipModule } from 'primeng/chip'
import { DividerModule } from 'primeng/divider'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { UserModel } from '../../../../core/models/user.model'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  finalize,
  map,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  zip,
} from 'rxjs'
import { UserService } from '../../../users/services/user.service'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputSwitchModule } from 'primeng/inputswitch'
import { CheckboxModule } from 'primeng/checkbox'
import { InputTextModule } from 'primeng/inputtext'
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular'
import { CalendarOptions, DatesSetArg } from '@fullcalendar/core/index.js'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { EventsService } from '../../../events/services/events.service'
import { AffairModel } from '../../../../core/models/affair.model'
import { ProductsService } from '../../../products/services/products.service'
import { DatePicker, DatePickerModule } from 'primeng/datepicker'
import { BadgeModule } from 'primeng/badge'
import { MessageModule } from 'primeng/message'
import { ContextMenuModule } from 'primeng/contextmenu'
import { MenuItem } from 'primeng/api'
import { DateTools } from '../../../../core/tools/date-tools'
import { CalendarTools } from '../../../../core/tools/calendar-tools'
import { TooltipModule } from 'primeng/tooltip'
import { EventDetailsModalComponent } from '../../../interventions/components/event-details-modal/event-details-modal.component'
import { RouteTools } from '../../../../core/tools/route-tools'
import { ToastService } from '../../../../shared/services/toast.service'

@Component({
  selector: 'app-affair-schedule-time-select',
  templateUrl: './affair-schedule-time-select.component.html',
  styleUrls: ['./affair-schedule-time-select.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DatePickerModule,
    ReactiveFormsModule,
    CardModule,
    DividerModule,
    ProgressSpinnerModule,
    ChipModule,
    FontAwesomeModule,
    IconFieldModule,
    InputIconModule,
    InputSwitchModule,
    InputTextModule,
    CheckboxModule,
    FullCalendarModule,
    BadgeModule,
    MessageModule,
    ContextMenuModule,
    TooltipModule,
    EventDetailsModalComponent,
  ],
})
export class AffairScheduleTimeSelectComponent
  implements OnInit, OnChanges, AfterViewInit
{
  private subscriptions: Subscription[] = []
  @Input() affair: AffairModel
  @Input() selectedTechnicians: UserModel[] = []
  @Input() selectedDateRange: Date[] = []
  @Input() users: any = []
  @Input() loadingUsers: boolean = false
  @Input() droppedEventDetails: any = null
  @Input() resetCalendar: boolean = false
  @Input() closestInterventions: any[] = []
  @Input() selectedProducts: any[] = []
  @Input() productGroups: any[] = []
  // Add new input property for returning to step flag
  @Input() returningToStep: boolean = false
  // Add new input for pre-selected slot from previous step
  @Input() preSelectedSlot: any = null

  @Output() dropDetails = new EventEmitter<any>()
  @Output() dateRangeChange = new EventEmitter<Date[]>()
  @Output() filtersChanged = new EventEmitter<any>()

  private destroyRef = inject(DestroyRef)
  private destroyed$ = new Subject<void>()

  private processedEventIds: Set<string> = new Set()
  private draggableInstance: any = null

  public rangeDates = new FormControl<Date[] | null>(null)
  public filtersForm: FormGroup
  public usersForm: FormGroup
  public datesSet$ = new Subject<DatesSetArg>()
  public calendarOptions: CalendarOptions = {
    initialView: 'timeGridWeek',
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    locale: frLocale,
    editable: true,
    droppable: true,
    firstDay: 1,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek',
    },
    height: '100',
    datesSet: (datesSetArg) => {
      this.ngZone.run(() => {
        // Mettre à jour la plage de dates actuelle du calendrier
        this.currentCalendarRange = {
          start: datesSetArg.start,
          end: datesSetArg.end,
        }
        this.datesSet$.next(datesSetArg)
      })
    },
    drop: (info) => {
      this.ngZone.run(() => {
        this.handleEventDrop(info)
      })
    },
    eventReceive: (info) => {
      this.ngZone.run(() => {
        this.handleEventReceive(info)
      })
    },
    eventChange: (info) => {
      this.ngZone.run(() => {
        this.handleEventChange(info)
      })
    },
    eventDrop: (info) => {
      this.ngZone.run(() => {
        this.handleExistingEventDrop(info)
      })
    },
    eventResize: (info) => {
      this.ngZone.run(() => {
        this.handleExistingEventResize(info)
      })
    },
    eventMouseEnter: (info) => {
      if (
        info.event.extendedProps &&
        info.event.extendedProps['productsData']
      ) {
        this.selectedEvent = info.event
      }
    },
    eventMouseLeave: (info) => {
      if (this.selectedEvent === info.event) {
        this.selectedEvent = null
      }
    },
    eventClick: (info) => {
      this.ngZone.run(() => {
        //this.handleEventClick(info)
      })
    },
    eventDidMount: (info) => {
      // Permettre l'édition de tous les événements
      // Les événements créés par l'utilisateur et les événements existants peuvent être déplacés
      const isUserCreated = info.event.classNames.includes('user-created-event')
      const isPreselected = info.event.classNames.includes('preselected-event')
      const eventData = info.event.extendedProps?.['data']
      const isExistingEvent =
        eventData?.resource === 'intervention' ||
        eventData?.resource === 'event'

      if (isUserCreated || isPreselected) {
        // Événements créés par l'utilisateur ou pré-sélectionnés - entièrement modifiables
        info.event.setProp('editable', true)
      } else if (isExistingEvent) {
        // Événements existants (interventions/events de l'API) - modifiables par drag & drop
        info.event.setProp('editable', true)
        // Ajouter une classe pour le style visuel (optionnel)
        info.el.classList.add('existing-event')
      } else {
        // Autres événements - non modifiables
        info.el.classList.add('non-editable-event')
        info.event.setProp('editable', false)
      }

      // Check if this event corresponds to a closest intervention
      if (this.closestInterventions && this.closestInterventions.length > 0) {
        const matchingIntervention = this.closestInterventions.find(
          (intervention) =>
            intervention.id === info.event.extendedProps['data']?.id
        )

        if (matchingIntervention) {
          // Utiliser des attributs de données et des classes au lieu de créer des éléments DOM
          info.el.classList.add('nearby-intervention')

          // Stocker les données comme attributs pour le styling CSS
          info.el.setAttribute(
            'data-distance',
            matchingIntervention.distance?.toString() || '??'
          )
        }
      }
    },
  }

  private eventsSubject = new BehaviorSubject<any[]>([])
  public fullCalendarEvents$ = this.eventsSubject.asObservable()
  // Ajouter une propriété pour stocker les événements actuels
  private currentEvents: any[] = []
  public loadingEvents = false
  @ViewChild(FullCalendarComponent) calendar: FullCalendarComponent
  @ViewChild('contextMenu') contextMenu: any
  @ViewChild('weekPickerCalendar') weekPickerCalendar: DatePicker

  public usersMultiSelectControl: FormControl
  public availableTechnicians: UserModel[] = []

  public hasDropEvent = false
  public hasSingleProductDropped = false
  public scheduledProductIds: Set<number> = new Set()
  public contextMenuItems: MenuItem[] = []
  public selectedEvent: any = null

  // Propriétés pour la sélection et le groupement des produits
  public selectedProductIds: Set<number> = new Set()

  // Propriétés pour la modale d'événement
  public showEventDialog: boolean = false
  public selectedEventDetails: any = {}

  // Ajouter cette propriété pour suivre la vue actuelle du calendrier
  public currentView: string = 'timeGridWeek'

  // Ajouter une propriété pour le technicien sélectionné pour les itinéraires
  public selectedTechForRoutes: UserModel | null = null

  // Timer pour le debounce de la notification d'itinéraire
  private routeNotificationTimer: any = null

  // Ajouter cette propriété pour stocker la plage de dates visible du calendrier
  public currentCalendarRange: { start: Date; end: Date } = {
    start: new Date(),
    end: new Date(new Date().setDate(new Date().getDate() + 7)),
  }

  // Properties for local storage
  private localStorageKey: string = ''

  // Store the ID of the pre-selected event for easy removal
  private preSelectedEventId: string | null = null

  constructor(
    private fb: FormBuilder,
    private usersService: UserService,
    private interventionsService: InterventionsService,
    private eventsService: EventsService,
    private productsService: ProductsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    // Initialize local storage key based on affair
    this.localStorageKey = `affair_schedule_time_${this.affair?.mlRef || 'default'}`

    // Initialize forms and event listeners first
    this.initForms()
    this.setupEventListeners()
    // Ne pas charger les événements ici, cela sera fait lors du passage à l'étape 3
    this.setupUsers()
    this.setupCalendarOptions()
    this.loadCalendarEvents()

    // Initialize properties that could cause ExpressionChangedAfterItHasBeenCheckedError
    this.selectedProductIds = new Set<number>()

    // Initialize selectedProductIds from selectedProducts (from step 1)
    this.initializeSelectedProductIds()

    // Restore pre-selected slot from local storage if available
    //this.restorePreSelectedSlotFromStorage()

    // Initialize draggable elements after a slight delay
    setTimeout(() => {
      this.initializeDraggable()

      // Apply pre-selected slot AFTER events are loaded, but only if no user events exist
      if (this.preSelectedSlot && !this.hasDropEvent) {
        setTimeout(() => {
          console.log('Applying preSelectedSlot:', this.preSelectedSlot)
          this.applyPreSelectedSlot(this.preSelectedSlot)
        }, 100)
      }
    }, 100)
  }

  /**
   * Initialize selectedProductIds from the selectedProducts input (from step 1)
   */
  private initializeSelectedProductIds(): void {
    if (this.selectedProducts && this.selectedProducts.length > 0) {
      this.selectedProductIds.clear()
      this.selectedProducts.forEach((product: any) => {
        this.selectedProductIds.add(product.id)
      })
    }
  }

  private initForms() {
    this.filtersForm = this.fb.group({
      departmentsCode: [[]],
      search: [''],
    })

    this.usersForm = this.fb.group({
      users: this.fb.control<UserModel[]>([]),
    })

    if (this.selectedTechnicians.length) {
      this.usersForm.get('users')?.setValue(this.selectedTechnicians)
    }

    if (this.selectedDateRange && this.selectedDateRange.length === 2) {
      this.rangeDates.setValue([
        new Date(this.selectedDateRange[0]),
        new Date(this.selectedDateRange[1]),
      ])
    }
  }

  private setupEventListeners() {
    this.contextMenuItems = [
      {
        label: 'Supprimer cet événement',
        icon: 'pi pi-trash',
        command: () => {
          if (this.selectedEvent) {
            this.removeEvent(this.selectedEvent)
            this.selectedEvent = null
          }
        },
      },
    ]

    this.destroyRef.onDestroy(() => {
      this.destroyed$.next()
      this.destroyed$.complete()
    })

    this.filtersForm.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe((filters) => {
        this.filtersChanged.emit(filters)
      })

    this.rangeDates.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((dates) => {
        if (dates && dates.length === 2 && DateTools.isValidDateRange(dates)) {
          try {
            const newStartDate = DateTools.toValidDate(dates[0])
            const newEndDate = DateTools.toValidDate(dates[1])

            if (newStartDate && newEndDate) {
              const normalizedStart = DateTools.normalizeDate(
                newStartDate,
                true
              )
              const normalizedEnd = DateTools.normalizeDate(newEndDate, false)

              this.dateRangeChange.emit([normalizedStart, normalizedEnd])
              this.updateCalendarViewBasedOnDateRange([
                normalizedStart,
                normalizedEnd,
              ])
            }
          } catch (error) {
            console.error('Error processing date range change:', error)
          }
        }
      })

    if (this.droppedEventDetails && !this.hasDropEvent) {
      this.hasDropEvent = true
    }
  }

  /**
   * Charge les événements du calendrier à la demande (ex: lors du passage à l'étape 3)
   * Appeler cette méthode explicitement depuis le parent.
   */
  public loadCalendarEvents(): void {
    const start =
      this.selectedDateRange && this.selectedDateRange.length > 0
        ? this.selectedDateRange[0]
        : new Date()
    const end =
      this.selectedDateRange && this.selectedDateRange.length > 1
        ? this.selectedDateRange[1]
        : new Date(new Date().setDate(new Date().getDate() + 7))
    const users =
      this.selectedTechnicians?.map((user: UserModel) => user.id) || []
    const params = {
      users,
      dateFrom: DateTools.formatDateForAPI(start),
      dateTo: DateTools.formatDateForAPI(end),
    }
    this.loadingEvents = true
    this.getEventsAndInterventionsForCalendar(params).subscribe((apiEvents) => {
      console.log("Événements récupérés de l'API:", apiEvents)

      // Récupérer les événements existants du calendrier avant de les écraser
      const existingUserEvents = this.preserveUserCreatedEvents()

      // Combiner les événements de l'API avec les événements utilisateur préservés
      const allEvents = [...apiEvents, ...existingUserEvents]
      this.currentEvents = allEvents

      // Émettre les événements via le BehaviorSubject
      this.eventsSubject.next(allEvents)

      this.loadingEvents = false

      if (this.droppedEventDetails) {
        console.log(
          'Processing dropped event details:',
          this.droppedEventDetails
        )
        this.processDroppedEventDetails(this.droppedEventDetails)
      }
      this.loadingEvents = false
      this.cdr.detectChanges()
    })
  }

  /**
   * Force le rafraîchissement des interventions et événements depuis l'API
   * Cette méthode recharge les données sans préserver les événements créés par l'utilisateur
   */
  public forceRefreshEvents(): void {
    const start =
      this.selectedDateRange && this.selectedDateRange.length > 0
        ? this.selectedDateRange[0]
        : new Date()
    const end =
      this.selectedDateRange && this.selectedDateRange.length > 1
        ? this.selectedDateRange[1]
        : new Date(new Date().setDate(new Date().getDate() + 7))
    const users =
      this.selectedTechnicians?.map((user: UserModel) => user.id) || []
    const params = {
      users,
      dateFrom: DateTools.formatDateForAPI(start),
      dateTo: DateTools.formatDateForAPI(end),
    }
    this.loadingEvents = true
    this.cdr.detectChanges()

    this.getEventsAndInterventionsForCalendar(params).subscribe({
      next: (apiEvents) => {
        console.log("Événements rafraîchis depuis l'API:", apiEvents)

        // Récupérer les événements utilisateur pour les préserver
        const existingUserEvents = this.preserveUserCreatedEvents()

        // Combiner les événements de l'API avec les événements utilisateur préservés
        const allEvents = [...apiEvents, ...existingUserEvents]
        this.currentEvents = allEvents

        // Émettre les événements via le BehaviorSubject
        this.eventsSubject.next(allEvents)

        this.loadingEvents = false
        this.cdr.detectChanges()

        this.toastService.showSuccess('Données actualisées avec succès')
      },
      error: (error) => {
        console.error('Erreur lors du rafraîchissement des événements:', error)
        this.loadingEvents = false
        this.cdr.detectChanges()
        this.toastService.showError(
          "Erreur lors de l'actualisation des données"
        )
      },
    })
  }

  private setupCalendarOptions() {
    this.calendarOptions = {
      ...this.calendarOptions,
      height: '100%',
      themeSystem: 'standard',
      slotEventOverlap: false,
      slotMinTime: '07:00:00',
      slotMaxTime: '20:00:00',
      locale: 'fr',
      defaultTimedEventDuration: '00:30:00',
      forceEventDuration: true,
      headerToolbar: false,
      buttonText: {
        today: "Aujourd'hui",
        month: 'Mois',
        week: 'Semaine',
        day: 'Jour',
      },
      editable: true,
      droppable: true,
      eventDragStart: (info) => {
        this.customizeDragImage(info)
      },
      eventDidMount: (info) => {
        // Check if this event corresponds to a closest intervention
        if (this.closestInterventions && this.closestInterventions.length > 0) {
          const matchingIntervention = this.closestInterventions.find(
            (intervention) =>
              intervention.id === info.event.extendedProps['data']?.id
          )
          if (matchingIntervention) {
            // Utiliser des attributs de données et des classes au lieu de créer des éléments DOM
            info.el.classList.add('nearby-intervention')

            // Stocker les données comme attributs pour le styling CSS
            info.el.setAttribute(
              'data-distance',
              matchingIntervention.distance?.toString() || '??'
            )

            // Obtenir les initiales du technicien
            const technician =
              matchingIntervention.usersTechnicians &&
              matchingIntervention.usersTechnicians.length > 0
                ? matchingIntervention.usersTechnicians[0]
                : null

            const technicianInitials = technician
              ? this.getTechnicianInitials(technician)
              : '??'

            info.el.setAttribute('data-tech-initials', technicianInitials)

            // Créer un élément qui contient l'info mais de manière invisible pour les lecteurs d'écran
            const a11yInfo = document.createElement('span')
            a11yInfo.className = 'sr-only'
            a11yInfo.textContent = `Intervention proche à ${matchingIntervention.distance}km, Technicien: ${technician?.firstname || ''} ${technician?.lastname || ''}`
            info.el.appendChild(a11yInfo)

            // Ajouter les informations de tooltip
            const tooltipText = `Intervention proche à ${matchingIntervention.distance}km
Technicien: ${technician?.firstname || ''} ${technician?.lastname || ''}
Affaire: ${matchingIntervention.affair?.mlRef || ''}`

            info.el.setAttribute('title', tooltipText)
          }
        }
      },
      // Configuration supplémentaire pour une meilleure gestion des événements
      timeZone: 'local', // Utiliser le fuseau horaire local
      nowIndicator: true, // Afficher un indicateur pour l'heure actuelle
      slotDuration: '00:30:00', // Durée de chaque intervalle
      snapDuration: '00:15:00', // Durée de chaque "snap"
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
      eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
      // Ajouter un handler pour mettre à jour la vue actuelle quand elle change
      viewDidMount: (info) => {
        this.ngZone.run(() => {
          this.currentView = info.view.type
        })
      },
    }
  }

  private setupUsers() {
    this.usersMultiSelectControl = this.usersForm.get('users') as FormControl
    this.prepareUserData()
    this.updateAvailableTechnicians()
  }

  onCalendarContextMenu(event: MouseEvent) {
    if (this.selectedEvent && this.selectedEvent.extendedProps?.productsData) {
      event.preventDefault()
      this.contextMenu.show(event)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle date range changes
    if (
      changes['selectedDateRange'] &&
      changes['selectedDateRange'].currentValue &&
      changes['selectedDateRange'].currentValue.length === 2
    ) {
      const newDates = changes['selectedDateRange'].currentValue.map(
        (d: any) => new Date(d)
      )

      this.rangeDates.setValue(newDates, { emitEvent: false })

      if (this.calendar?.getApi()) {
        const api = this.calendar.getApi()
        api.gotoDate(newDates[0])
        this.updateCalendarViewBasedOnDateRange(newDates)
      }
    }

    // Handle technician changes
    if (
      changes['selectedTechnicians'] &&
      changes['selectedTechnicians'].currentValue
    ) {
      if (this.usersForm) {
        this.usersForm
          .get('users')
          ?.setValue(changes['selectedTechnicians'].currentValue)
      }

      // Automatically select the first technician for routes when the list changes
      if (this.selectedTechnicians?.length > 0) {
        this.selectedTechForRoutes = this.selectedTechnicians[0]
      } else {
        this.selectedTechForRoutes = null
      }
    }

    // Handle users changes
    if (changes['users'] && changes['users'].currentValue) {
      this.prepareUserData()
      this.updateAvailableTechnicians()
    }

    // Handle selectedProducts changes (from step 1)
    if (
      changes['selectedProducts'] &&
      changes['selectedProducts'].currentValue
    ) {
      this.initializeSelectedProductIds()
    }

    // Handle returning to this step after navigation
    /*if (
      changes['returningToStep'] &&
      changes['returningToStep'].currentValue === true
    ) {
      setTimeout(() => {
        if (this.droppedEventDetails) {
          this.processDroppedEventDetails(this.droppedEventDetails)
        }
      }, 300)
    }*/

    // Handle dropped event details
    /*if (changes['droppedEventDetails']) {
      const currentValue = changes['droppedEventDetails'].currentValue

      if (currentValue) {
        // Remove the !this.hasDropEvent condition as it prevents restoration
        this.processDroppedEventDetails(currentValue)
      } else if (changes['droppedEventDetails'].previousValue) {
        // droppedEventDetails is now null but was previously set
        // This means we need to remove the dropped event from calendar
        this.removeDroppedEventFromCalendar()
        this.hasDropEvent = false
        this.dropDetails.emit(null)
      }
    }*/

    // Handle calendar reset
    if (
      changes['resetCalendar'] &&
      changes['resetCalendar'].currentValue === true
    ) {
      this.clearCalendarEvents()
      this.hasDropEvent = false
      this.preSelectedEventId = null // Clear the stored event ID
      this.dropDetails.emit(null)
    }

    // Handle pre-selected slot changes
    if (changes['preSelectedSlot']) {
      const newSlot = changes['preSelectedSlot'].currentValue
      if (newSlot && !this.hasDropEvent) {
        // Save to local storage when receiving new pre-selected slot
        this.savePreSelectedSlotToStorage()
        // The slot will be applied after events are loaded in setupCalendarEvents
      } else if (!newSlot) {
        this.preSelectedEventId = null // Clear the stored event ID
        this.clearPreSelectedSlotFromStorage()
      }
    }
  }

  private processDroppedEventDetails(eventDetails: any) {
    this.hasDropEvent = true

    if (!eventDetails) {
      return
    }

    // Check if this exact event already exists in the current events
    const uniqueId = eventDetails.uniqueId

    // Check for event with same unique ID in currentEvents
    const existingEvent = uniqueId
      ? this.currentEvents.find((e) => e.extendedProps?.uniqueId === uniqueId)
      : null

    if (existingEvent) {
      return
    }

    // Check for event with same products in currentEvents
    const productIds = eventDetails.productsData?.map((p: any) => p.id) || []
    if (productIds.length > 0) {
      const sameEventExists = this.currentEvents.some((event) => {
        const eventProductIds =
          event.extendedProps?.productsData?.map((p: any) => p.id) || []
        return (
          eventProductIds.length > 0 &&
          productIds.every((id: any) => eventProductIds.includes(id)) &&
          eventProductIds.every((id: any) => productIds.includes(id))
        )
      })

      if (sameEventExists) {
        return
      }
    }

    // If we get here, we need to restore the event
    this.restoreDroppedEvent(eventDetails)
    this.cdr.detectChanges()
  }

  ngAfterViewInit() {
    this.initializeCalendarAfterView()
  }

  private initializeCalendarAfterView() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          if (this.calendar?.getApi() && this.selectedDateRange.length === 2) {
            this.updateCalendarViewBasedOnDateRange(this.selectedDateRange)

            /*if (this.droppedEventDetails && this.hasDropEvent) {
              setTimeout(() => {
                this.restoreDroppedEvent(this.droppedEventDetails)
              }, 100)
            }*/

            this.cdr.detectChanges()
          }
        })
      }, 0)
    })

    // Re-initialize draggable after content loads
    setTimeout(() => {
      setTimeout(() => {
        if (document.querySelectorAll('.product-group-item').length > 0) {
          this.initializeDraggable()
        }
      }, 1000)
    }, 500)
  }

  private prepareDragEventData(el: HTMLElement): any {
    if (
      el.classList.contains('disabled-product') ||
      this.hasSingleProductDropped
    ) {
      return null
    }

    if (el.classList.contains('product-item')) {
      return this.prepareSingleProductDragData(el)
    } else {
      return this.prepareGroupProductsDragData(el)
    }
  }

  private prepareSingleProductDragData(el: HTMLElement): any {
    const productId = el.getAttribute('data-product')
    const productData = this.selectedProducts.find(
      (p: any) => p.id == productId
    )
    const durationMinutes = productData?.product.duration || 0

    return {
      title: productData?.product.name || '',
      duration: { seconds: durationMinutes * 60 },
      extendedProps: {
        productsData: [productData],
        totalDurationMinutes: durationMinutes,
      },
      classNames: ['custom-event-drag'],
      html: `<div class="fc-event-title">${productData?.product.name || ''}</div>
            <div class="event-duration-badge">${durationMinutes} min</div>`,
    }
  }

  private prepareGroupProductsDragData(el: HTMLElement): any {
    const productIdsStr = el.getAttribute('data-products') || ''
    const isManualGroup = el.getAttribute('data-is-manual') === 'true'
    const groupId = el.getAttribute('data-group-id')

    if (!productIdsStr) {
      console.error('No product IDs found for this group')
      return {
        title: 'Produits groupés',
        duration: { seconds: 3600 },
        extendedProps: { productsData: [] },
      }
    }

    const productIds = productIdsStr.split(',').map((id) => Number(id.trim()))

    const productsData = this.selectedProducts.filter((p: any) =>
      productIds.includes(p.id)
    )

    const totalDurationMinutes = productsData.reduce(
      (sum: number, p: any) => sum + (p.product.duration || 0),
      0
    )

    const productsNames = this.formatGroupName(productsData)
    const groupName = productsNames || 'Produits groupés'

    return {
      title: groupName,
      duration: { seconds: totalDurationMinutes * 60 },
      extendedProps: {
        productsData: productsData,
        totalDurationMinutes: totalDurationMinutes,
        isManualGroup: isManualGroup,
        groupId: groupId,
      },
      classNames: ['custom-event-drag', 'group-event'],
      html: `<div class="fc-event-title">${groupName}</div>
            <div class="event-duration-badge">${totalDurationMinutes} min</div>`,
    }
  }

  getEventsAndInterventionsForCalendar(params: {
    users: number[]
    dateFrom: string
    dateTo: string
  }) {
    this.loadingEvents = true
    return zip([
      this.interventionsService.getInterventions(params),
      this.eventsService.getEvents(params),
    ]).pipe(
      finalize(() => (this.loadingEvents = false)),
      map(([interventions, events]) => {
        // Normaliser les dates des événements en objets Date JavaScript
        const normalizedEvents = [...interventions, ...events].map((event) => {
          if (event.start && typeof event.start === 'string') {
            // Convertir les chaînes de date en objets Date
            event.start = this.parseApiDateString(event.start)
          }
          if (event.end && typeof event.end === 'string') {
            // Convertir les chaînes de date en objets Date
            event.end = this.parseApiDateString(event.end)
          }
          return event
        })
        return normalizedEvents
      })
    )
  }

  /**
   * Analyse une chaîne de date provenant de l'API et la convertit en objet Date
   * Gère à la fois le format ISO et le format personnalisé "YYYY-MM-DD HH:mm:ss"
   */
  private parseApiDateString(dateStr: string): Date {
    if (!dateStr) return new Date()

    // Si la chaîne contient déjà un 'T' et 'Z', c'est probablement déjà un format ISO
    if (dateStr.includes('T')) {
      return new Date(dateStr)
    }

    // Format "YYYY-MM-DD HH:mm:ss"
    const [datePart, timePart] = dateStr.split(' ')
    if (datePart && timePart) {
      const [year, month, day] = datePart.split('-').map(Number)
      const [hours, minutes, seconds] = timePart.split(':').map(Number)

      // Mois en JavaScript commence à 0, donc on soustrait 1
      return new Date(year, month - 1, day, hours, minutes, seconds)
    }

    // Fallback
    return new Date(dateStr)
  }

  removeUser(user: UserModel) {
    const selectedUsers = this.usersForm.controls['users'].value ?? []
    this.usersForm.controls['users'].setValue(
      selectedUsers.filter((u: any) => u.id !== user.id)
    )
    this.updateAvailableTechnicians()
  }

  formatDate(date: Date | null): string {
    return DateTools.formatDateForAPI(date as Date)
  }

  // Event handlers
  handleEventDrop(info: any) {
    this.hasDropEvent = true
    setTimeout(() => {
      CalendarTools.clearDuplicateEvents(this.calendar?.getApi())
    }, 200)
  }

  handleEventReceive(info: any) {
    const event = info.event
    const productIds =
      event.extendedProps.productsData?.map((p: any) => p.id) || []
    const eventId = DateTools.generateEventId(productIds)

    // Check for duplicates
    const productIdSignature = DateTools.createProductSignature(productIds)
    if (this.processedEventIds.has(productIdSignature)) {
      event.remove()
      return
    }

    // Add to processed events and clear after a delay
    this.processedEventIds.add(productIdSignature)
    setTimeout(() => {
      this.processedEventIds.clear()
    }, 300)

    // Update state
    this.hasDropEvent = true
    this.droppedEventDetails = null
    const isProductGroup = event.extendedProps?.productsData?.length > 1
    this.hasSingleProductDropped = !isProductGroup
    const preserveOnReload = event.extendedProps?.preserveOnReload

    // Set event properties
    const classNames = ['user-created-event']
    event.setProp('classNames', classNames)

    // Rendre explicitement l'événement modifiable
    event.setProp('editable', true)
    // Get ERP source from technicians
    const erpSource = this.getDefaultErpSource()
    // Set ERP source on all products
    this.setErpSourceOnProducts(event.extendedProps.productsData, erpSource)
    // Add product IDs to scheduled list
    this.addProductsToScheduled(event.extendedProps.productsData)

    // Calculate duration and set end time
    const totalDurationMinutes = this.calculateEventDuration(event)

    // S'assurer que la date de début est correctement formatée
    const startTime = new Date(event.start)

    // Calculer la date de fin correcte
    const endTime = this.calculateEventEndTime(startTime, totalDurationMinutes)

    // Appliquer la date de fin à l'événement
    event.setEnd(endTime)

    if (preserveOnReload) {
      event.setExtendedProp('preserveOnReload', preserveOnReload)
    }

    // Create drop details object
    const dropDetails = {
      title: event.title,
      date: event.start,
      productsData: event.extendedProps.productsData,
      duration: totalDurationMinutes,
      end: endTime,
      erpSource: erpSource,
      isProductGroup: isProductGroup,
      isAlreadyInCalendar: true,
      uniqueId: eventId,
    }

    // Set unique ID on event
    event.setExtendedProp('uniqueId', eventId)
    this.preSelectedEventId = eventId

    // Emit drop details
    this.ngZone.run(() => {
      this.dropDetails.emit(dropDetails)

      // Mettre à jour currentEvents pour le tracking interne uniquement
      // NE PAS appeler eventsSubject.next() car FullCalendar a déjà ajouté l'événement visuellement
      // et cela écraserait les positions des événements existants qui ont été déplacés
      this.currentEvents = [
        ...this.currentEvents,
        {
          id: eventId,
          title: event.title,
          start: event.start,
          end: event.end,
          extendedProps: event.extendedProps,
          classNames: ['user-created-event'], // Forcer le tableau avec la classe
          isUserCreated: true, // Flag explicite
        },
      ]

      this.cdr.detectChanges()

      // Notifier l'utilisateur que l'itinéraire a été recalculé
      this.notifyRouteRecalculated()
    })
  }

  private getDefaultErpSource(): string {
    return this.selectedTechnicians && this.selectedTechnicians.length > 0
      ? this.selectedTechnicians[0].erpSource
      : 'mylink'
  }

  private setErpSourceOnProducts(products: any[], erpSource: string) {
    if (!products) return

    products.forEach((product: any) => {
      if (!product.erpSource) {
        product.erpSource = erpSource
      }
    })
  }

  private addProductsToScheduled(products: any[]) {
    if (!products) return

    const productIds = products.map((p: any) => p.id)
    productIds.forEach((id) => this.scheduledProductIds.add(Number(id)))
  }

  private calculateEventDuration(event: any): number {
    return (
      event.extendedProps.totalDurationMinutes ||
      event.extendedProps.productsData?.reduce(
        (sum: number, product: any) => sum + (product.product.duration || 0),
        0
      ) ||
      60
    )
  }

  private calculateEventEndTime(
    startTime: Date,
    durationMinutes: number
  ): Date {
    // Cloner la date de début pour éviter toute modification par référence
    const endTime = new Date(startTime.getTime())

    // Ajouter les minutes à la date de début
    endTime.setMinutes(endTime.getMinutes() + durationMinutes)

    return endTime
  }

  handleEventChange(info: any) {
    const event = info.event

    const updatedDetails = {
      title: event.title,
      date: event.start,
      productsData: event.extendedProps.productsData,
      duration: event.duration,
      end: event.end,
    }

    // Mettre à jour l'événement dans currentEvents pour que l'itinéraire soit recalculé
    const eventId = event.id || event.extendedProps?.uniqueId
    if (eventId) {
      const eventIndex = this.currentEvents.findIndex(
        (e) => e.id === eventId || e.extendedProps?.uniqueId === eventId
      )
      if (eventIndex !== -1) {
        this.currentEvents[eventIndex] = {
          ...this.currentEvents[eventIndex],
          start: event.start,
          end: event.end,
        }
      }
    }

    this.ngZone.run(() => {
      this.dropDetails.emit(updatedDetails)
      this.cdr.detectChanges()

      // Notifier l'utilisateur que l'itinéraire a été recalculé
      this.notifyRouteRecalculated()
    })
  }

  /**
   * Handle drag and drop of existing events (interventions/events from the API)
   * Similar to the calendar component's handleEventDrop
   */
  handleExistingEventDrop(info: any): void {
    const { event } = info

    // Skip user-created events - they are handled by handleEventChange
    if (event.classNames.includes('user-created-event')) {
      return
    }

    const eventData = event.extendedProps?.data
    if (!eventData) {
      info.revert()
      return
    }

    const interventionId = eventData.id
    const erpSource = eventData.erpSource
    const resource = eventData.resource

    const startDate = DateTools.toLocalISOString(event.start)
    const endDate = DateTools.toLocalISOString(event.end)
    const duration = DateTools.calcTimeDifference(
      new Date(startDate),
      new Date(endDate)
    )

    this.loadingEvents = true

    const params = {
      users: this.selectedTechnicians?.map((user) => user.id) ?? [],
      startDate: startDate,
      erpSource: erpSource,
      duration: duration.toTimeString(),
    }

    if (resource === 'intervention') {
      this.interventionsService
        .updateDate(interventionId, params)
        .pipe(finalize(() => (this.loadingEvents = false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess(
              'Intervention mise à jour avec succès'
            )
            // Mettre à jour les données de l'événement dans currentEvents pour garder la synchronisation
            this.updateEventInCurrentEvents(
              interventionId,
              event.start,
              event.end
            )
            // Notifier l'utilisateur que l'itinéraire a été recalculé
            this.notifyRouteRecalculated()
          },
          error: (err) => {
            this.toastService.showError(
              "Erreur lors de la mise à jour de l'intervention"
            )
            info.revert()
          },
        })
    } else if (resource === 'event') {
      this.eventsService
        .updateDate(interventionId, params)
        .pipe(finalize(() => (this.loadingEvents = false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess('Événement mis à jour avec succès')
            // Mettre à jour les données de l'événement dans currentEvents pour garder la synchronisation
            this.updateEventInCurrentEvents(
              interventionId,
              event.start,
              event.end
            )
            // Notifier l'utilisateur que l'itinéraire a été recalculé
            this.notifyRouteRecalculated()
          },
          error: (err) => {
            this.toastService.showError(
              "Erreur lors de la mise à jour de l'événement"
            )
            info.revert()
          },
        })
    } else {
      this.loadingEvents = false
      info.revert()
    }
  }

  /**
   * Handle resize of existing events (interventions/events from the API)
   * Reuses the same logic as handleExistingEventDrop
   */
  handleExistingEventResize(info: any): void {
    // Reuse the same logic as drop
    this.handleExistingEventDrop(info)
  }

  /**
   * Met à jour les données d'un événement dans currentEvents après un déplacement réussi
   * Cela permet de garder la synchronisation locale sans recharger depuis l'API
   */
  private updateEventInCurrentEvents(
    eventId: number,
    newStart: Date,
    newEnd: Date
  ): void {
    const eventIndex = this.currentEvents.findIndex(
      (e) => e.extendedProps?.data?.id === eventId
    )

    if (eventIndex !== -1) {
      // Mettre à jour les dates dans le tableau local
      this.currentEvents[eventIndex] = {
        ...this.currentEvents[eventIndex],
        start: newStart,
        end: newEnd,
      }

      // Mettre à jour également les données internes si elles existent
      if (this.currentEvents[eventIndex].extendedProps?.data) {
        this.currentEvents[eventIndex].extendedProps.data.startDate = newStart
        this.currentEvents[eventIndex].extendedProps.data.endDate = newEnd
      }
    }
  }

  updateCalendarViewBasedOnDateRange(dateRange: Date[]) {
    if (!dateRange || dateRange.length < 2 || !this.calendar?.getApi()) return

    this.ngZone.runOutsideAngular(() => {
      const calendarApi = this.calendar.getApi()
      const startDate = new Date(dateRange[0])
      const endDate = new Date(dateRange[1])
      const diffDays = Math.ceil(
        Math.abs(endDate.getTime() - startDate.getTime()) /
          (1000 * 60 * 60 * 24)
      )

      // Choose appropriate view based on date range
      calendarApi.changeView(diffDays <= 7 ? 'timeGridWeek' : 'dayGridMonth')
      calendarApi.gotoDate(startDate)

      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.detectChanges()
        })
      }, 0)
    })
  }

  getTechnicianInitials(technician: any): string {
    if (technician.initials) return technician.initials
    if (!technician.firstname || !technician.lastname) return '??'
    return (
      technician.firstname.charAt(0).toUpperCase() +
      technician.lastname.charAt(0).toUpperCase()
    )
  }

  prepareUserData() {
    // Add null check here too
    if (!this.users || !this.users.length) return

    this.users.forEach((user: any) => {
      user.fullName =
        user.firstname && user.lastname
          ? `${user.firstname} ${user.lastname}`
          : 'Technicien'
    })
  }

  updateAvailableTechnicians() {
    // Add a null check before filtering
    if (!this.users || !Array.isArray(this.users)) {
      this.availableTechnicians = []
      return
    }

    const selectedIds = new Set(
      (this.usersForm.value.users || []).map((user: any) => user.id)
    )

    this.availableTechnicians = this.users.filter(
      (user: any) => !selectedIds.has(user.id)
    )
  }

  onTechniciansSelectionChange(event: any) {
    if (!event || !event.value) return

    const selectedIds = event.value
    const techniciansToAdd = this.availableTechnicians.filter((tech: any) =>
      selectedIds.includes(tech.id)
    )

    if (techniciansToAdd.length) {
      const currentUsers = this.usersForm.get('users')?.value || []
      this.usersForm
        .get('users')
        ?.setValue([...currentUsers, ...techniciansToAdd])

      setTimeout(() => {
        this.usersMultiSelectControl.setValue([])
      }, 0)
    }
  }

  restoreDroppedEvent(eventDetails: any) {
    if (!eventDetails) {
      return
    }

    try {
      const { title, date, productsData, uniqueId } = eventDetails
      const eventDate = new Date(date)

      // Check for duplicates in currentEvents
      if (uniqueId) {
        const duplicateEvent = this.currentEvents.find(
          (e) => e.extendedProps?.uniqueId === uniqueId
        )
        if (duplicateEvent) {
          return
        }
      }

      const productIds = productsData?.map((p: any) => p.id) || []
      if (productIds.length > 0) {
        const eventWithSameProducts = this.currentEvents.some((event) => {
          const eventProductIds =
            event.extendedProps?.productsData?.map((p: any) => p.id) || []
          return (
            eventProductIds.length > 0 &&
            productIds.every((id: any) => eventProductIds.includes(id)) &&
            eventProductIds.every((id: any) => productIds.includes(id))
          )
        })

        if (eventWithSameProducts) {
          return
        }
      }

      // Update state
      const isProductGroup = productsData?.length > 1
      this.hasSingleProductDropped = !isProductGroup
      this.addProductsToScheduled(productsData)

      // Get ERP source
      const erpSource = this.getDefaultErpSource()

      // Set ERP source on products
      this.setErpSourceOnProducts(productsData, erpSource)

      // Calculate duration and end time
      const totalDurationMinutes =
        productsData?.reduce(
          (sum: number, product: any) => sum + (product.product.duration || 0),
          0
        ) || 60

      const endDate = this.calculateEventEndTime(
        eventDate,
        totalDurationMinutes
      )

      // Generate or use existing unique ID
      const eventId = uniqueId || DateTools.generateEventId(productIds)

      // Create event object
      const newEvent = {
        title: title || 'Événement restauré',
        start: eventDate.toISOString(),
        end: endDate.toISOString(),
        allDay: false,
        editable: true,
        extendedProps: {
          productsData: productsData,
          totalDurationMinutes: totalDurationMinutes,
          erpSource: erpSource,
          uniqueId: eventId,
        },
        classNames: ['user-created-event'],
      }

      // Add to currentEvents and update BehaviorSubject
      const allCurrentEvents = [...this.currentEvents]
      allCurrentEvents.push(newEvent)

      this.currentEvents = allCurrentEvents
      this.eventsSubject.next(allCurrentEvents)

      // Navigate to the event date if calendar is available
      if (this.calendar?.getApi()) {
        this.calendar.getApi().gotoDate(eventDate)
      }

      // Emit updated event details
      this.dropDetails.emit({
        ...eventDetails,
        uniqueId: eventId,
      })

      this.cdr.detectChanges()
    } catch (error) {
      console.error('Error restoring dropped event:', error)
    }
  }

  private findEventByUniqueId(calendarApi: any, uniqueId: string): any {
    const existingEvents = calendarApi.getEvents()
    return existingEvents.find(
      (e: any) => e.extendedProps && e.extendedProps['uniqueId'] === uniqueId
    )
  }

  private hasEventWithSameProducts(
    calendarApi: any,
    productIds: number[]
  ): boolean {
    const existingEvents = calendarApi.getEvents()
    const productIdSignature = DateTools.createProductSignature(productIds)

    return existingEvents.some((e: any) => {
      const eventProductIds =
        e.extendedProps?.['productsData']?.map((p: any) => p.id) || []
      const eventSignature = DateTools.createProductSignature(eventProductIds)
      return eventSignature === productIdSignature
    })
  }

  clearCalendarEvents(): void {
    // Clear all events using BehaviorSubject approach
    this.scheduledProductIds.clear()
    this.hasSingleProductDropped = false
    this.preSelectedEventId = null // Clear the stored event ID
    this.currentEvents = []
    this.eventsSubject.next([]) // Émettre un tableau vide
  }

  removeDroppedEventFromCalendar(): void {
    // Find and remove events that were dropped (not pre-selected) from currentEvents
    const filteredEvents = this.currentEvents.filter((event) => {
      const extendedProps = event.extendedProps || {}

      // Keep events that are pre-selected or original interventions/events
      if (
        extendedProps['isPreSelected'] ||
        extendedProps['isOriginalIntervention'] ||
        extendedProps['isOriginalEvent'] ||
        !extendedProps['productsData']
      ) {
        return true
      }

      // Remove products from scheduled list for events being removed
      if (extendedProps['productsData']) {
        const productIds = extendedProps['productsData'].map((p: any) => p.id)
        productIds.forEach((productId: number) => {
          this.scheduledProductIds.delete(productId)
        })
      }

      return false // Remove this event
    })

    // Update the events
    this.currentEvents = filteredEvents
    this.eventsSubject.next(filteredEvents)

    // Reset drop flags
    this.hasSingleProductDropped = false

    // Also clear any saved dropped event data from parent stepper localStorage
    // This prevents the event from being restored when the calendar reloads
    this.clearDroppedEventFromStepperStorage()
  }

  private clearDroppedEventFromStepperStorage(): void {
    try {
      // Try to access the parent stepper's localStorage key
      const stepperKey = `affair-schedule-${this.affair?.mlRef}`
      const savedState = localStorage.getItem(stepperKey)

      if (savedState) {
        const state = JSON.parse(savedState)

        // Clear dropped event data
        if (state.droppedDetails) {
          state.droppedDetails = null
        }
        if (state.hasDroppedEvent !== undefined) {
          state.hasDroppedEvent = false
        }
        if (state.formData && state.formData.step2) {
          state.formData.step2.droppedDetails = null
          state.formData.step2.hasDroppedEvent = false
        }

        // Save back to localStorage
        localStorage.setItem(stepperKey, JSON.stringify(state))
      }
    } catch (error) {
      console.error('Error clearing dropped event from stepper storage:', error)
    }
  }

  isProductScheduled(productId: number): boolean {
    return this.scheduledProductIds.has(Number(productId))
  }

  get unscheduledProducts(): any[] {
    return (this.selectedProducts || []).filter(
      (p: any) => !this.isProductScheduled(p.id)
    )
  }

  get unscheduledProductGroups(): any[] {
    // If productGroups is empty or null, return empty array
    if (!this.productGroups || this.productGroups.length === 0) {
      return []
    }

    // Otherwise return only unscheduled product groups
    return this.productGroups.filter(
      (group) =>
        !group.products.some((product: any) =>
          this.isProductScheduled(product.id)
        )
    )
  }

  getProductIdsString(products: any[]): string {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return ''
    }

    return products
      .map((product) => product?.id?.toString() || '')
      .filter((id) => id)
      .join(',')
  }

  customizeDragImage(info: any) {
    setTimeout(() => {
      const dragEl = document.querySelector('.fc-event.fc-event-dragging')
      if (!dragEl) return

      const event = info.event
      const totalDurationMinutes = event.extendedProps?.totalDurationMinutes

      if (totalDurationMinutes) {
        if (!dragEl.querySelector('.event-duration-badge')) {
          const durationBadge = document.createElement('div')
          durationBadge.className = 'event-duration-badge'
          durationBadge.textContent = `${totalDurationMinutes} min`
          dragEl.appendChild(durationBadge)
        }
      }
    }, 0)
  }

  removeEvent(event: any) {
    // Get product IDs
    const productIds =
      event.extendedProps?.productsData?.map((p: any) => p.id) || []
    // Remove products from scheduled list
    productIds.forEach((id: number) =>
      this.scheduledProductIds.delete(Number(id))
    )

    // Supprimer l'événement de currentEvents pour que l'itinéraire soit mis à jour
    const eventId = event.id || event.extendedProps?.uniqueId
    if (eventId) {
      this.currentEvents = this.currentEvents.filter(
        (e) => e.id !== eventId && e.extendedProps?.uniqueId !== eventId
      )
    } else {
      // Fallback: supprimer par correspondance de produits
      const productSignature = productIds.sort().join(',')
      this.currentEvents = this.currentEvents.filter((e) => {
        const eProductIds =
          e.extendedProps?.productsData?.map((p: any) => p.id) || []
        const eSignature = eProductIds.sort().join(',')
        return eSignature !== productSignature
      })
    }

    // Remove the event from calendar
    event.remove()
    // Reset state
    this.hasSingleProductDropped = false
    this.hasDropEvent = false
    this.droppedEventDetails = null
    // Notify parent
    this.dropDetails.emit(null)
    // Update UI
    this.cdr.detectChanges()
  }

  canDragProduct(isGroup: boolean): boolean {
    return !this.hasSingleProductDropped
  }

  initializeDraggable() {
    this.ngZone.run(() => {
      setTimeout(() => {
        const container = document.getElementById('external-events')
        if (!container) {
          console.error('Container external-events not found')
          return
        }

        // Créer une nouvelle instance draggable
        this.draggableInstance = CalendarTools.initializeProductDraggable(
          container,
          this.selectedProducts,
          (el: any) => this.prepareDragEventData(el)
        )
      }, 100)
    })
  }

  destroyDraggableCompletely() {
    if (this.draggableInstance) {
      try {
        this.draggableInstance.destroy()
        this.draggableInstance = null
      } catch (e) {
        console.error('Error destroying draggable instance:', e)
      }
    }
  }

  /**
   * Formats the group name based on the product names
   * @param products Array of product objects
   * @returns Formatted name string
   */
  private formatGroupName(products: any[]): string {
    return (
      products
        .map((p) => p.product?.name || 'Produit')
        .join(', ')
        .substring(0, 60) + (products.length > 2 ? '...' : '')
    )
  }

  /**
   * Vérifie si un jour spécifique a des interventions pour un technicien donné
   * Inclut les événements créés par l'utilisateur
   * @param date La date à vérifier
   * @param technician Le technicien à vérifier
   * @returns true si des interventions existent, false sinon
   */
  dayHasInterventions(date: Date, technician: UserModel | null): boolean {
    if (
      !technician ||
      !date ||
      !this.currentEvents ||
      this.currentEvents.length === 0
    ) {
      return false
    }

    return this.currentEvents.some((event) => {
      // Vérifier si l'événement a lieu à la date spécifiée
      const eventDate = new Date(event.start)
      const isSameDay = this.isSameDay(eventDate, date)

      if (!isSameDay) return false

      // Vérifier si c'est un événement créé par l'utilisateur
      const isUserCreated =
        event.isUserCreated === true ||
        event.classNames?.includes('user-created-event') ||
        (Array.isArray(event.classNames) &&
          event.classNames.some((c: string) => c === 'user-created-event'))

      // Les événements créés par l'utilisateur sont toujours considérés comme valides pour l'itinéraire
      if (isUserCreated) {
        return true
      }

      // Vérifier si l'événement est associé au technicien
      let isForTechnician = false

      // Vérifier dans data.usersTechnicians (format API)
      if (
        event.data &&
        event.data.usersTechnicians &&
        Array.isArray(event.data.usersTechnicians)
      ) {
        isForTechnician = event.data.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      // Vérifier dans usersTechnicians direct (format interne)
      if (
        !isForTechnician &&
        event.usersTechnicians &&
        Array.isArray(event.usersTechnicians)
      ) {
        isForTechnician = event.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      return isSameDay && isForTechnician
    })
  }

  /**
   * Notifie l'utilisateur que l'itinéraire a été recalculé et l'incite à cliquer sur le bouton dédié
   * Utilise un debounce pour éviter les notifications multiples
   */
  notifyRouteRecalculated(): void {
    // Annuler le timer précédent s'il existe
    if (this.routeNotificationTimer) {
      clearTimeout(this.routeNotificationTimer)
    }

    // Déclencher la notification après un court délai (debounce)
    this.routeNotificationTimer = setTimeout(() => {
      this.toastService.showInfo(
        "Itinéraire recalculé ! Cliquez sur les boutons d'itinéraire au-dessus du calendrier pour le consulter."
      )
      this.routeNotificationTimer = null
    }, 300)
  }

  /**
   * Ouvre l'URL de l'itinéraire journalier dans un nouvel onglet
   * @param technician Le technicien pour l'itinéraire
   * @param date La date pour laquelle générer l'itinéraire
   */
  openDailyRouteUrl(technician: UserModel | any, date: Date): void {
    // Vérifier si le jour contient des interventions
    if (!this.dayHasInterventions(date, technician)) {
      console.warn("Pas d'interventions pour ce jour et ce technicien")
      alert(
        "Aucune intervention n'est programmée pour ce technicien à cette date"
      )
      return
    }

    if (
      !technician ||
      !date ||
      !this.currentEvents ||
      this.currentEvents.length === 0
    ) {
      console.error("Données manquantes pour générer l'URL d'itinéraire", {
        technician,
        date,
        eventsCount: this.currentEvents?.length || 0,
      })

      // Afficher une alerte pour l'utilisateur
      alert(
        "Impossible de générer l'itinéraire. Veuillez vérifier que le technicien a bien des interventions programmées à cette date."
      )
      return
    }

    const url = this.generateDailyRouteUrl(technician, date)

    if (!url || url === 'https://www.google.com/maps/dir/') {
      console.error("URL d'itinéraire invalide générée")
      alert('Aucune intervention trouvée pour ce technicien à cette date')
      return
    }

    window.open(url, '_blank')
  }

  /**
   * Génération d'URL de routing journalier pour un technicien
   * Inclut les événements créés par l'utilisateur avec l'adresse de l'affaire courante
   * Les waypoints sont triés par heure de début des événements
   * @param technician Le technicien pour l'itinéraire
   * @param date La date pour laquelle générer l'itinéraire
   * @returns URL de l'itinéraire Google Maps
   */
  generateDailyRouteUrl(technician: UserModel | any, date: Date): string {
    if (!technician || !date || !this.currentEvents) {
      return ''
    }

    const dateString = DateTools.formatDateForAPI(date)

    // Filtrer les événements pour ce jour
    const eventsForDay = this.currentEvents.filter((event) => {
      const eventDate = new Date(event.start)
      const eventDateString = DateTools.formatDateForAPI(eventDate)
      return eventDateString === dateString
    })

    if (eventsForDay.length === 0) {
      return ''
    }

    // Construire l'URL
    let mapsUrl = 'https://www.google.com/maps/dir/?api=1'

    // Adresse du technicien comme origine et destination
    const technicianAddress = encodeURIComponent(
      [technician.postalCode || '', technician.locality || '']
        .filter(Boolean)
        .join(', ')
    )

    mapsUrl += `&origin=${technicianAddress}`
    mapsUrl += `&destination=${technicianAddress}`

    // Construire les waypoints avec leur timestamp pour le tri
    const waypointsWithTime: { address: string; startTime: number }[] = []

    eventsForDay.forEach((event) => {
      const eventStartTime = new Date(event.start).getTime()

      // Vérifier si c'est un événement créé par l'utilisateur
      const isUserCreated =
        event.isUserCreated === true ||
        event.classNames?.includes('user-created-event') ||
        (Array.isArray(event.classNames) &&
          event.classNames.some((c: string) => c === 'user-created-event'))

      if (isUserCreated) {
        // Pour les événements créés par l'utilisateur, utiliser l'adresse de l'affaire
        if (this.affair) {
          const affairAddress = [
            this.affair.workSitePostalCode || '',
            this.affair.workSiteLocality || '',
          ]
            .filter(Boolean)
            .join(', ')

          if (affairAddress) {
            waypointsWithTime.push({
              address: encodeURIComponent(affairAddress),
              startTime: eventStartTime,
            })
          }
        }
      } else if (event.data && event.data.resource === 'intervention') {
        // Pour les interventions existantes, vérifier si le technicien est assigné
        const hasTechnician = event.data.usersTechnicians?.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )

        if (hasTechnician) {
          const address = [
            event.data.workSitePostalCode || '',
            event.data.workSiteLocality || '',
          ]
            .filter(Boolean)
            .join(', ')

          if (address) {
            waypointsWithTime.push({
              address: encodeURIComponent(address),
              startTime: eventStartTime,
            })
          }
        }
      }
    })

    // Trier les waypoints par heure de début
    waypointsWithTime.sort((a, b) => a.startTime - b.startTime)

    // Extraire les adresses triées
    const waypoints = waypointsWithTime.map((w) => w.address)

    if (waypoints.length > 0) {
      mapsUrl += '&waypoints=' + waypoints.join('|')
    }

    mapsUrl += '&travelmode=driving'

    return mapsUrl
  }

  /**
   * Formate l'affichage des dates pour la modale
   */
  formatDateRange(
    start: string | Date | null,
    end: string | Date | null
  ): string {
    if (!start || !end) {
      return 'Date non spécifiée'
    }

    try {
      const startDate = typeof start === 'string' ? new Date(start) : start
      const endDate = typeof end === 'string' ? new Date(end) : end

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return 'Date invalide'
      }

      const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }

      return `${startDate.toLocaleDateString('fr-FR', options)} - ${endDate.toLocaleDateString(
        'fr-FR',
        options
      )}`
    } catch (error) {
      console.error('Erreur lors du formatage de la plage de dates:', error)
      return 'Erreur de format de date'
    }
  }

  /**
   * Formate l'adresse pour l'affichage
   */
  formatAddress(intervention: any): string {
    if (!intervention) return 'Adresse non disponible'

    const parts = [
      intervention.workSiteAddress,
      intervention.workSitePostalCode,
      intervention.workSiteLocality,
    ].filter(Boolean)

    return parts.join(', ')
  }

  /**
   * Extrait la référence ML de la description
   */
  extractMlRef(description: string): string | null {
    if (!description) return null

    // Essaie de faire correspondre un modèle comme ML24012121
    const match = description.match(/\bML\d+\b/)
    return match ? match[0] : null
  }

  /**
   * Formate les noms des techniciens en une chaîne
   */
  formatTechnicianNames(technicians: any[]): string {
    return technicians
      .map((tech: any) => `${tech.firstname} ${tech.lastname}`)
      .join(', ')
  }

  /**
   * Calcule la durée totale des produits
   */
  calculateTotalDuration(products: any[]): number {
    return products.reduce(
      (sum, product) => sum + (product.product?.duration || 0),
      0
    )
  }

  /**
   * Formate la description des produits
   */
  formatProductsDescription(products: any[]): string {
    if (!products || products.length === 0) {
      return 'Aucun produit sélectionné'
    }

    return products
      .map((p) => {
        const name = p.product?.name || 'Produit sans nom'
        const duration = p.product?.duration || 0
        return `${name} (${duration} min)`
      })
      .join('\n')
  }

  /**
   * Récupère tous les jours de la période actuelle affichée dans le calendrier
   * @returns Un tableau d'objets contenant les informations sur chaque jour
   */
  getWeekDays(): {
    date: Date
    shortName: string
    longFormat: string
    isToday: boolean
    hasInterventions: boolean
  }[] {
    const result = []
    const today = new Date()

    // Utiliser les dates du calendrier actuel, avec un maximum de 7 jours
    let startDate = new Date(this.currentCalendarRange.start)
    let endDate = new Date(this.currentCalendarRange.end)

    // Limiter à 7 jours maximum
    const maxDays = 7
    const totalDays = Math.min(
      maxDays,
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      )
    )

    // Si nous sommes en vue mois, montrer les 7 jours à partir d'aujourd'hui
    if (this.currentView === 'dayGridMonth') {
      startDate = new Date()
    }

    // Générer les jours
    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)

      // Formater le nom court du jour (L, M, M, J, V, S, D)
      const shortName = this.getDayShortName(date.getDay())

      // Format long pour le tooltip (ex: "Lundi 28 mars")
      const longFormat = this.formatDayForTooltip(date)

      // Vérifier si c'est aujourd'hui
      const isToday = this.isSameDay(date, today)

      // Vérifier si ce jour a des interventions pour le technicien sélectionné
      const hasInterventions = this.dayHasInterventions(
        date,
        this.selectedTechForRoutes ||
          (this.selectedTechnicians.length === 1
            ? this.selectedTechnicians[0]
            : null)
      )

      result.push({
        date,
        shortName,
        longFormat,
        isToday,
        hasInterventions,
      })
    }

    return result
  }

  /**
   * Récupère le nom court du jour de la semaine
   * @param dayIndex L'index du jour (0 = dimanche, 1 = lundi, etc.)
   * @returns Le nom court du jour
   */
  private getDayShortName(dayIndex: number): string {
    const shortNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S']
    return shortNames[dayIndex]
  }

  /**
   * Formate la date pour l'affichage dans le tooltip
   * @param date La date à formater
   * @returns La date formatée (ex: "Lundi 28 mars")
   */
  private formatDayForTooltip(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }
    return date.toLocaleDateString('fr-FR', options)
  }

  /**
   * Vérifie si deux dates sont le même jour
   * @param date1 Première date
   * @param date2 Deuxième date
   * @returns true si les deux dates sont le même jour
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }

  /**
   * Sélectionne un technicien pour afficher ses itinéraires
   * @param technician Le technicien à sélectionner
   */
  selectTechnicianForRoutes(technician: UserModel): void {
    this.selectedTechForRoutes = technician
  }

  /**
   * Change la vue du calendrier
   * @param viewName Le nom de la vue à activer (dayGridMonth, timeGridWeek, timeGridDay)
   */
  changeCalendarView(viewName: string): void {
    if (!this.calendar?.getApi()) return

    this.calendar.getApi().changeView(viewName)
    this.currentView = viewName

    // Mettre à jour les jours affichés lorsque la vue change
    this.cdr.detectChanges()
  }

  /**
   * Navigue vers une date spécifique dans le calendrier
   * @param date La date à afficher
   */
  goToDate(date: Date): void {
    if (!this.calendar?.getApi()) return

    this.calendar.getApi().gotoDate(date)

    // Si on est en vue mois, passer en vue jour pour cette date
    if (this.currentView === 'dayGridMonth') {
      this.changeCalendarView('timeGridDay')
    }
  }

  /**
   * Apply a pre-selected slot from the previous step to the calendar
   * @param preSelectedSlotData The pre-selected slot data
   */
  applyPreSelectedSlot(preSelectedSlotData: any): void {
    console.log('applyPreSelectedSlot called with:', preSelectedSlotData)

    if (
      !preSelectedSlotData ||
      !preSelectedSlotData.slot ||
      !preSelectedSlotData.technician
    ) {
      console.log('applyPreSelectedSlot: Invalid slot data, returning')
      return
    }
    const slot = preSelectedSlotData.slot
    const technician = preSelectedSlotData.technician

    // Use the products selected in step 1 (stored in selectedProductIds)
    let productToSchedule = null

    if (this.selectedProductIds.size === 0) {
      console.log('applyPreSelectedSlot: No selected products, returning')
      return
    }

    console.log(
      'applyPreSelectedSlot: selectedProductIds:',
      this.selectedProductIds
    )
    console.log(
      'applyPreSelectedSlot: selectedProducts:',
      this.selectedProducts
    )

    // Find the products that were selected in step 1
    const selectedProductsFromStep1 = this.selectedProducts.filter(
      (product: any) => this.selectedProductIds.has(product.id)
    )

    if (selectedProductsFromStep1.length === 0) {
      return
    }

    // Check if we have a matching product group from step 1
    const matchingProductGroup = this.productGroups.find(
      (group: any) =>
        group.products.every((product: any) =>
          this.selectedProductIds.has(product.id)
        ) && group.products.length === this.selectedProductIds.size
    )

    if (matchingProductGroup) {
      // Use the existing product group from step 1
      productToSchedule = matchingProductGroup
    } else {
      // Create a product group from the selected products in step 1
      const totalDuration = selectedProductsFromStep1.reduce(
        (sum: number, product: any) => sum + (product.product.duration || 0),
        0
      )

      const productsNames = selectedProductsFromStep1
        .map((p: any) => p.product?.name || 'Produit')
        .join(', ')

      productToSchedule = {
        id: `step1_group_${Date.now()}`,
        products: selectedProductsFromStep1,
        productsNames: productsNames,
        totalDuration: totalDuration,
        isFromStep1: true,
      }
    }

    if (!productToSchedule) {
      return
    }

    // Check if an event with this pre-selected data already exists
    const api = this.calendar.getApi()
    const existingEvents = api.getEvents()

    const alreadyExists = existingEvents.some(
      (event) =>
        (event.extendedProps?.['isPreSelected'] &&
          event.extendedProps?.['preSelectedSlot']?.start === slot.start) ||
        (event.classNames?.includes('preselected-event') &&
          event.start?.getTime() === new Date(slot.start).getTime())
    )

    if (alreadyExists) {
      return
    }

    // Ensure the technician is selected
    if (!this.selectedTechnicians.find((t) => t.id === technician.id)) {
      this.selectedTechnicians.push(technician)
      this.usersForm.get('users')?.setValue(this.selectedTechnicians)
    }

    // Create an event on the calendar at the pre-selected time
    const startDate = new Date(slot.start)

    // Calculate the correct end time immediately based on products duration
    const totalDurationMinutes = productToSchedule.totalDuration
    const endDate = new Date(
      startDate.getTime() + totalDurationMinutes * 60 * 1000
    )

    // Prepare products data exactly like in drag-and-drop
    const productsData = productToSchedule.products.map((product: any) => ({
      ...product,
      erpSource: this.getDefaultErpSource(), // Set ERP source like in normal drop
    }))

    // Create a simulated event that mimics what handleEventReceive expects
    const simulatedEvent = {
      id: `preselected_${Date.now()}`,
      title: productToSchedule.productsNames,
      start: startDate,
      end: endDate, // Use calculated end date directly
      classNames: ['user-created-event'], // Add both classes
      editable: true,
      extendedProps: {
        productsData: productsData,
        totalDurationMinutes: totalDurationMinutes,
        isPreSelected: true, // Mark as pre-selected
        preSelectedSlot: slot, // Store original slot data
        preSelectedTechnician: technician, // Store technician data
        preserveOnReload: true, // Flag to preserve during reloads
      } as any,
      // Mock FullCalendar event methods that handleEventReceive uses
      setProp: (propName: string, value: any) => {
        if (propName === 'classNames') {
          simulatedEvent.classNames = Array.isArray(value) ? value : [value]
        } else if (propName === 'editable') {
          simulatedEvent.editable = value
        }
      },
      setEnd: (endTime: Date) => {
        simulatedEvent.end = endTime
      },
      setExtendedProp: (propName: string, value: any) => {
        ;(simulatedEvent.extendedProps as any)[propName] = value
      },
    }

    // Create the info object that handleEventReceive expects
    const eventReceiveInfo = {
      event: simulatedEvent as any,
    }

    // Call handleEventReceive directly - it will handle adding the event to the calendar
    if (this.calendar?.getApi()) {
      const api = this.calendar.getApi()

      // Now call handleEventReceive as if this was a manual drag-and-drop
      this.handleEventReceive(eventReceiveInfo)

      // Navigate to the event date
      api.gotoDate(startDate)

      // Set appropriate view
      if (this.currentView === 'dayGridMonth') {
        api.changeView('timeGridWeek')
        this.currentView = 'timeGridWeek'
      }
    }

    // Save pre-selected slot to local storage
    this.savePreSelectedSlotToStorage()
  }

  /**
   * Save the current pre-selected slot to local storage
   */
  private savePreSelectedSlotToStorage(): void {
    if (this.preSelectedSlot && this.localStorageKey) {
      try {
        const slotData = {
          ...this.preSelectedSlot,
          selectedProductIds: Array.from(this.selectedProductIds), // Save selected products
          timestamp: Date.now(),
        }
        localStorage.setItem(this.localStorageKey, JSON.stringify(slotData))
      } catch (error) {
        console.error('Error saving pre-selected slot to local storage:', error)
      }
    }
  }

  /**
   * Preserve user-created events (including pre-selected ones) during API reloads
   */
  private preserveUserCreatedEvents(): any[] {
    if (!this.calendar?.getApi()) {
      return []
    }

    const calendar = this.calendar.getApi()
    const existingEvents = calendar.getEvents()

    // Filter for user-created events (including pre-selected events)
    // If droppedEventDetails is null, don't preserve dropped events
    const userCreatedEvents = existingEvents.filter((event) => {
      // Always preserve pre-selected events
      if (
        event.id?.toString().startsWith('preselected_') ||
        event.extendedProps?.['isPreSelected']
      ) {
        return true
      }

      // Always preserve explicitly user-created events
      if (event.classNames?.includes('user-created-event')) {
        return true
      }

      // Only preserve dropped events (with productsData) if we still have droppedEventDetails
      if (event.extendedProps?.['productsData']) {
        return this.droppedEventDetails !== null
      }

      return false
    })

    // Convert FullCalendar events back to plain objects for the events source
    return userCreatedEvents.map((event) => ({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      classNames: [...(event.classNames || []), 'preserved-user-event'],
      extendedProps: {
        ...event.extendedProps,
        isUserCreated: true,
        preservedAt: new Date().toISOString(),
      },
      editable: event.classNames?.includes('user-created-event') || true,
      backgroundColor: event.backgroundColor || '#3788d8',
      borderColor: event.borderColor || '#3788d8',
    }))
  }

  /**
   * Check if there are any user-created events on the calendar
   */
  private hasUserCreatedEvents(): boolean {
    if (!this.calendar?.getApi()) {
      return false
    }

    const calendar = this.calendar.getApi()
    const existingEvents = calendar.getEvents()

    return existingEvents.some(
      (event) =>
        event.classNames?.includes('user-created-event') ||
        event.id?.toString().startsWith('preselected_') ||
        event.extendedProps?.['productsData']
    )
  }

  /**
   * Clear pre-selected slot from local storage
   */
  private clearPreSelectedSlotFromStorage(): void {
    if (this.localStorageKey) {
      try {
        localStorage.removeItem(this.localStorageKey)
      } catch (error) {
        console.error(
          'Error clearing pre-selected slot from local storage:',
          error
        )
      }
    }
  }

  /**
   * Remove the pre-selected slot and allow manual drag-and-drop
   */
  public removePreSelectedSlot(): void {
    // Clear the pre-selected slot
    this.preSelectedSlot = null

    // Remove the pre-selected event using its stored ID (much simpler!)
    const calendar = this.calendar?.getApi()
    if (calendar) {
      if (!this.preSelectedEventId) {
        // No pre-selected event ID stored, nothing to remove
      } else {
        // First try to find by FullCalendar event id
        let preSelectedEvent: any =
          calendar.getEventById(this.preSelectedEventId) || null

        // If not found, try fallback: find event whose extendedProps.uniqueId matches
        if (!preSelectedEvent) {
          const events = calendar.getEvents()

          // Robust matching: try several ways to match the stored id to a calendar event
          preSelectedEvent =
            events.find((e: any) => {
              try {
                // 1) Direct match with FullCalendar event id
                if (
                  e.id &&
                  this.preSelectedEventId &&
                  e.id.toString() === this.preSelectedEventId
                ) {
                  return true
                }

                const ext = e.extendedProps || {}

                // 2) Direct match with extendedProps.uniqueId
                if (
                  this.preSelectedEventId &&
                  ext['uniqueId'] === this.preSelectedEventId
                ) {
                  return true
                }
              } catch (err) {
                // ignore and continue
              }

              return false
            }) || null
        }

        if (preSelectedEvent) {
          // Remove products from scheduled list
          if (preSelectedEvent.extendedProps?.['productsData']) {
            const productIds = preSelectedEvent.extendedProps[
              'productsData'
            ].map((p: any) => p.id)
            productIds.forEach((productId: number) => {
              this.scheduledProductIds.delete(productId)
            })
          }

          // Remove the event from calendar
          preSelectedEvent.remove()

          // Force calendar to re-render
          calendar.render()
        }

        // Clear the stored event ID
        this.preSelectedEventId = null
      }
    } else {
      console.warn(
        'Calendar API not available when trying to remove pre-selected event'
      )
    }

    // Clear from local storage
    this.clearPreSelectedSlotFromStorage()

    // Reset drop event flag to allow new manual drops
    this.hasDropEvent = false
    this.hasSingleProductDropped = false

    // Clear drop details
    this.dropDetails.emit(null)

    // Trigger change detection immediately
    this.cdr.detectChanges()
  }

  /**
   * Handle click on calendar to detect week number clicks
   * @param event The click event
   */
  onCalendarClick(event: MouseEvent): void {
    const target = event.target as HTMLElement

    // Check if the clicked element is a week number cell
    if (
      target.classList.contains('p-datepicker-weeknumber') ||
      target.closest('.p-datepicker-weeknumber')
    ) {
      // Find the row that was clicked
      const row = target.closest('tr')
      if (row) {
        // Get the current month and year from the DatePicker component
        const viewMonth =
          this.weekPickerCalendar?.currentMonth ?? new Date().getMonth()
        const viewYear =
          this.weekPickerCalendar?.currentYear ?? new Date().getFullYear()

        // Get all day cells in the row (excluding week number cell)
        const dayCells = row.querySelectorAll(
          'td:not(.p-datepicker-weeknumber)'
        )

        if (dayCells.length === 7) {
          const weekDates: Date[] = []

          dayCells.forEach((cell, index) => {
            const span = cell.querySelector('span')
            if (span) {
              const dayNum = parseInt(span.textContent || '0', 10)
              if (dayNum > 0) {
                let cellMonth = viewMonth
                let cellYear = viewYear

                // Check if this cell is from another month by looking at the TD element
                const isOtherMonth = cell.classList.contains(
                  'p-datepicker-other-month'
                )

                if (isOtherMonth) {
                  // Use position in the row to determine if it's previous or next month
                  // Days at the beginning of the row (index 0-2) with high day numbers = previous month
                  // Days at the end of the row (index 4-6) with low day numbers = next month
                  if (index < 3 && dayNum > 20) {
                    // Previous month
                    cellMonth = viewMonth - 1
                    if (cellMonth < 0) {
                      cellMonth = 11
                      cellYear = viewYear - 1
                    }
                  } else if (index > 3 && dayNum < 10) {
                    // Next month
                    cellMonth = viewMonth + 1
                    if (cellMonth > 11) {
                      cellMonth = 0
                      cellYear = viewYear + 1
                    }
                  }
                }

                weekDates.push(new Date(cellYear, cellMonth, dayNum))
              }
            }
          })

          if (weekDates.length > 0) {
            // Get the first and last dates of the week
            const startDate = weekDates[0]
            const endDate = weekDates[weekDates.length - 1]

            // Set the date range
            this.rangeDates.setValue([startDate, endDate])
          }
        }
      }
    }
  }
}
